import { ApolloLink, NextLink, Operation } from "@apollo/client";
import type {
  DocumentNode,
  ListTypeNode,
  NamedTypeNode,
  OperationDefinitionNode,
  TypeNode,
} from "graphql";

/**
 * Dev-only guard against the class of bug that shipped a numeric string
 * (e.g. '1354') into a Float/Int GraphQL variable and relied on
 * graphql-core 2's lenient coercion to save it. graphql-core 3 (pulled in
 * by the Django 2.2 -> 4.2 upgrade) rejects that at the wire level with a
 * 400, but by then a developer is staring at a generic network-error toast
 * far from the offending form field. This walks the operation's own
 * variable-definitions AST -- the same document Apollo is about to send --
 * and throws immediately, naming the offending variable, so the mistake
 * surfaces at the call site in development instead of on staging.
 *
 * Limitation: only top-level variables (and lists of scalars) are checked.
 * Validating fields *inside* input objects (e.g. `location.latitude`) would
 * require resolving each InputObjectType field's declared type, which is
 * only available via schema introspection -- and this link has no live
 * schema reference at runtime. Nested input-object validation is
 * intentionally not attempted; keep that in mind if a future bug hides
 * inside a nested input field.
 */

const SCALAR_VALIDATORS: Record<string, (value: unknown) => boolean> = {
  Float: (value) => typeof value === "number" && Number.isFinite(value),
  Int: (value) =>
    typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value),
  Boolean: (value) => typeof value === "boolean",
};

function describeValue(value: unknown): string {
  if (value === null) return "null";
  const type = typeof value;
  if (type === "string") return `string ${JSON.stringify(value)}`;
  return `${type} ${JSON.stringify(value)}`;
}

function unwrapNonNull(typeNode: TypeNode): NamedTypeNode | ListTypeNode {
  return typeNode.kind === "NonNullType" ? typeNode.type : typeNode;
}

function checkValueAgainstType(
  typeNode: TypeNode,
  value: unknown,
  path: string
): string | null {
  const unwrapped = unwrapNonNull(typeNode);

  if (unwrapped.kind === "ListType") {
    if (!Array.isArray(value)) return null; // shape mismatch is the server's problem, not ours
    for (let index = 0; index < value.length; index += 1) {
      const element = value[index];
      if (element === undefined || element === null) continue;
      const error = checkValueAgainstType(
        unwrapped.type,
        element,
        `${path}[${index}]`
      );
      if (error) return error;
    }
    return null;
  }

  // NamedType: only scalars we police (Float/Int/Boolean) are checked.
  // String/ID/enums/input objects are left alone.
  const typeName = unwrapped.name.value;
  const validator = SCALAR_VALIDATORS[typeName];
  if (!validator || validator(value)) return null;
  return `variable "${path}" expects ${typeName} but received ${describeValue(
    value
  )}`;
}

/**
 * Pure check, exported for unit testing. Returns a human-readable message
 * describing the first offending variable, or null if every variable
 * present in `variables` is compatible with its declared type in `query`.
 */
export function findVariableTypeViolation(
  query: DocumentNode,
  variables: Record<string, unknown> | undefined | null,
  operationName?: string | null
): string | null {
  if (!variables) return null;

  const operationDefinitions = query.definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === "OperationDefinition"
  );

  // Apollo sends the request's variables against a single operation - a
  // request document with an operationName targets exactly that operation,
  // and an unnamed request only makes sense with exactly one operation in
  // the document. Validating every operation in the document (e.g. a
  // co-located fragment/query pair) would check variables against
  // definitions that never receive them, producing false positives.
  const operationDefinition = operationName
    ? operationDefinitions.find((def) => def.name?.value === operationName)
    : operationDefinitions.length === 1
    ? operationDefinitions[0]
    : undefined;

  if (!operationDefinition) return null;

  for (const variableDefinition of operationDefinition.variableDefinitions ??
    []) {
    const name = variableDefinition.variable.name.value;
    if (!Object.prototype.hasOwnProperty.call(variables, name)) continue;
    const value = variables[name];
    if (value === undefined || value === null) continue;

    const error = checkValueAgainstType(
      variableDefinition.type,
      value,
      `$${name}`
    );
    if (error) {
      const opLabel = operationName ? `"${operationName}"` : "(anonymous)";
      return `[variableTypeValidationLink] Operation ${opLabel}: ${error}.`;
    }
  }

  return null;
}

/**
 * Dev-only Apollo Link that throws when an operation's variables don't
 * match their declared Float/Int/Boolean types. Registration is gated on
 * `process.env.NODE_ENV === "development"` in client.ts -- this link is
 * inert (never imported into the production link chain) in production
 * builds.
 */
export const variableTypeValidationLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    const violation = findVariableTypeViolation(
      operation.query,
      operation.variables,
      operation.operationName
    );
    if (violation) {
      throw new Error(violation);
    }
    return forward(operation);
  }
);
